import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Select from "react-select";
import LeftSidePopupLayout from "../../../../component/popup/modal-layout";
import XCloseIcon from "../../../../component/icons/x-close";
import Style from "./style.module.css";
import { GlobalContext } from "../../../../context/Context";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
} from "../../../../component/form/form-layout";
import { Loader2 } from "lucide-react";

const AddWallet = ({
  onSubmit = () => { },
  onClose = () => { },
  onError = () => { },
  selectedCustomerId = null, // if already selected
}) => {
  const { state } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // ✅ Fetch customers only if no customer is pre-selected
  useEffect(() => {
    if (!selectedCustomerId) {
      const fetchCustomers = async () => {
        try {
          setLoadingCustomers(true);
          const res = await apiCall({
            method: "get",
            url: "users/customers-list",
          });

          setCustomers(
            (res?.data || []).map((c) => ({
              value: c.userid,
              label: c.name,
            }))
          );
        } catch (error) {
          console.error("Error loading customers:", error);
          onError("Failed to load customers.");
        } finally {
          setLoadingCustomers(false);
        }
      };

      fetchCustomers();
    }
  }, [selectedCustomerId]);

  // ✅ Validation schema
  const validationSchema = yup.object({
    customer_id: yup
      .mixed()
      .required("Please select a customer"),
    current_balance: yup
      .number()
      .typeError("Enter a valid amount")
      .min(0, "Amount cannot be negative")
      .required("This field is required"),
    reason: yup.string().required("Please enter a reason"),
  });

  const formik = useFormik({
    initialValues: {
      customer_id: selectedCustomerId || null,
      current_balance: "",
      reason: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const data = {
          customer_id: selectedCustomerId
            ? selectedCustomerId
            : values.customer_id?.value,
          current_balance: values.current_balance,
          reason: values.reason,
        };

        const res = await apiCall({
          method: "post",
          url: "wallet/add-wallet",
          data,
        });

        formik.resetForm();
        onSubmit(res?.message || "Wallet added successfully!");
      } catch (error) {
        console.error("Error adding wallet:", error);
        onError(
          error?.response?.data?.message || "Failed to add wallet."
        );
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        {/* Header */}
        <div
          className={`${Style.modal_header} ${Style.addMeModal_head} flex justify-between items-center`}
        >
          <h4>Add Wallet</h4>
          <div
            onClick={onClose}
            className={`${Style.close_btn} cursor-pointer`}
          >
            <XCloseIcon />
          </div>
        </div>

        {/* Body */}
        <div className={`${Style.addMeModal_body}`}>
          <div className={Style.dcmb__card}>
            <div className={Style.dcmb__cardBody}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* Customer dropdown (only if not pre-selected) */}
                  {!selectedCustomerId && (
                    <FeildWraper width="100%">
                      <FormLabel required>Select Customer</FormLabel>

                      <Select
                        name="customer_id"
                        className="text-sm"
                        classNamePrefix="react-select"
                        isLoading={loadingCustomers}
                        options={customers}
                        components={{
                          IndicatorSeparator: () => null,
                          LoadingIndicator: () => null,
                        }}
                        value={formik.values.customer_id}
                        onChange={(val) =>
                          formik.setFieldValue("customer_id", val)
                        }
                        placeholder={
                          loadingCustomers
                            ? "Loading..."
                            : "Select Customer..."
                        }
                        isClearable
                        menuPlacement="auto"
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "35px",
                            borderRadius: "5px",
                            border: "1px solid #DFDFDF",
                            boxShadow: state.isFocused ? "none" : "none",
                            "&:hover": { borderColor: "#DFDFDF" },
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#1E1E1E",
                            backgroundColor: "#FFF",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 6px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px",
                            color: "#1E1E1E",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontSize: "14px",
                            color: "#717680",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 99999,
                          }),
                        }}
                      />

                      {formik.touched.customer_id &&
                        formik.errors.customer_id && (
                          <FeildError>{formik.errors.customer_id}</FeildError>
                        )}
                    </FeildWraper>
                  )}

                  {/* Current Balance */}
                  <FeildWraper width="100%">
                    <FormLabel required>Initial Balance</FormLabel>
                    <input
                      type="number"
                      name="current_balance"
                      placeholder="Enter amount"
                      value={formik.values.current_balance}
                       onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      className="border rounded-lg px-3 py-2 w-full"
                    />
                    {formik.touched.current_balance &&
                      formik.errors.current_balance && (
                        <FeildError>{formik.errors.current_balance}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Reason */}
                  <FeildWraper width="100%">
                    <FormLabel required>Reason</FormLabel>
                    <textarea
                      name="reason"
                      rows={4}
                      placeholder="Enter reason for wallet creation"
                      value={formik.values.reason}
                      onChange={formik.handleChange}
                       onBlur={formik.handleBlur}
                    ></textarea>
                    {formik.touched.reason && formik.errors.reason && (
                      <FeildError>{formik.errors.reason}</FeildError>
                    )}
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={Style.dcmb__cardFoot}>
          <button
            type="reset"
            className={Style.dcmb__cancel}
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="submit"
            className={Style.dcmb__submit}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
            ) : (
              "Add Wallet"
            )}
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default AddWallet;
